import axios, { AxiosError } from 'axios';
import {
  AddressFromZipcodeType,
  BillFinancingInfoResponseType,
  BillingType,
  BillType,
  CityType,
  ContactType,
  ContractType,
  FinancingBillingCreationType,
  FinancingInfoResponseType,
  OfferType,
  OrderAddressType,
  OrderCreationType,
  OrderType,
  OrderUpdateAddressType,
  OrderUpdateType,
  PaymentType,
  StateType,
  UpfrontBillingCreationType,
} from 'src/types';

import { AnalysisType } from 'src/types/Analysis';
import { SalesFunnelType } from 'src/types/SalesFunnel';
import { ContractTypes, PaymentMethodEnum } from 'src/utils/constants/types';
import { camelToSnakeCase, snakeToCamelCase } from 'src/utils/functions';

export const platformAPI = axios.create({
  baseURL: process.env.CHECKOUT_API_URL || process.env.NEXT_PUBLIC_CHECKOUT_API_URL,
  withCredentials: true,
});

platformAPI.interceptors.request.use(
  (config) => {
    if (config.params) {
      config.params = camelToSnakeCase(config.params);
    }

    if (config.data) {
      config.data = camelToSnakeCase(config.data);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

platformAPI.interceptors.response.use(
  (response) => {
    if (response.data && response.headers['content-type'].indexOf('application/json') > -1) {
      response.data = snakeToCamelCase(response.data);
    }

    return response;
  },
  async (error: AxiosError) => {
    if (error.response && error.response.data instanceof Blob) {
      try {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);

        return Promise.reject({
          ...error,
          response: {
            ...error.response,
            data: errorJson,
          },
        });
      } catch (blobError) {
        return Promise.reject(error);
      }
    }

    if (error.response?.status >= 500 && error.response?.status <= 599) {
      console.error(
        JSON.stringify({
          message: 'Axios error',
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
            response: {
              status: error.response?.status,
              data: error.response?.data,
              headers: error.response?.headers,
            },
          },
          context: {
            method: error.config?.method,
            url: error.config?.url,
            data: error?.config?.data,
          },
        }),
      );
    }

    return Promise.reject(error);
  },
);

export const getCurrentContract = async (contractType: ContractTypes, contractData?: any): Promise<ContractType> => {
  const { data } = await platformAPI.get('/contracts/current', {
    data: { contractType, contractData: contractData || {} },
  });

  return data;
};

export const getOffer = async (id: string): Promise<OfferType> => {
  const { data } = await platformAPI.get(`/offers/${id}`);

  return data;
};

export const getCities = async (stateId: string): Promise<Array<CityType>> => {
  const { data } = await platformAPI.get('/cities', { params: { stateId } });

  return data;
};

export const getStates = async (): Promise<Array<StateType>> => {
  const { data } = await platformAPI.get('/states');

  return data;
};

export const getZipCode = async (zipCode: string): Promise<AddressFromZipcodeType> => {
  const { data } = await platformAPI.get(`/zipcodes/${zipCode}`);

  return data;
};

export const createOrder = async (order: OrderCreationType): Promise<OrderType> => {
  const { data } = await platformAPI.post('/orders', order);

  return data;
};

export const updateOrder = async (order: OrderUpdateType): Promise<OrderType> => {
  const { id, ...orderData } = order;
  const { data } = await platformAPI.patch(`/orders/${id}`, orderData);

  return data;
};

export const createOrderAddress = async (order: OrderUpdateAddressType): Promise<OrderAddressType> => {
  const { id, ...addressData } = order;
  const { data } = await platformAPI.post(`/orders/${id}/addresses`, addressData);
  return data;
};

type UpdateContactParamsType = {
  id: string;
  contactType: string;
  address: string;
};

export const updateContact = async (params: UpdateContactParamsType): Promise<OrderType> => {
  const { id, ...contact } = params;
  const { data } = await platformAPI.put(`/orders/${params.id}/update_contact`, contact);

  return data;
};

export const getOrderAnalyses = async (orderId: string): Promise<AnalysisType[]> => {
  const { data } = await platformAPI.get('/analyses', { params: { orderId } });

  return data;
};

export const getOrder = async (orderId: string): Promise<OrderType> => {
  const { data } = await platformAPI.get(`/orders/${orderId}`);

  return data;
};

export const downloadBoleto = async (billId: string): Promise<Blob> => {
  const { data } = await platformAPI.get(`/bills/${billId}/download_boleto`, {
    responseType: 'blob',
  });

  return data;
};

export const getOfferUpfrontInfo = async (offerId: string): Promise<BillFinancingInfoResponseType[]> => {
  const { data } = await platformAPI.get(`/offers/${offerId}/upfront_info`);

  return data;
};

export const getOrderFinancingInfo = async (orderId: string): Promise<FinancingInfoResponseType[]> => {
  const { data } = await platformAPI.get(`/orders/${orderId}/financing_info`);

  return data;
};

export const getBillFinancingInfo = async (billId: string): Promise<BillFinancingInfoResponseType[]> => {
  const { data } = await platformAPI.get(`/bills/${billId}/financing_info`);

  return data;
};

export const createValidationCode = async ({
  contactId,
  channel,
}: {
  contactId: string;
  channel: string;
}): Promise<void> =>
  await platformAPI.post(`/contacts/${contactId}/send_validation_token`, {
    communicationChannel: channel,
  });

export const validateCode = async ({
  contactId,
  confirmationCode,
}: {
  contactId: string;
  confirmationCode: string;
}): Promise<ContactType> => {
  const { data } = await platformAPI.put(`/contacts/${contactId}/validate_token`, {
    validationToken: confirmationCode,
  });

  return data;
};

export const getBill = async (id: string): Promise<{ bill: BillType; order: OrderType }> => {
  const { data } = await platformAPI.get(`/bills/${id}`);

  return data;
};

export type CreatePaymentConfigs = {
  paymentMethod: PaymentMethodEnum;
  cardToken?: string;
  installments?: number;
};

export const createPayment = async ({
  billId,
  configs,
}: {
  billId: string;
  configs: CreatePaymentConfigs;
}): Promise<PaymentType> => {
  const { data } = await platformAPI.post(`/payments`, {
    billId,
    configs,
  });

  return data;
};

export const getSalesFunnel = async (offerId: string): Promise<SalesFunnelType> => {
  const { data } = await platformAPI.get(`/offers/${offerId}/current_sales_funnel`);

  return data;
};

export const createBilling = async (
  billingCreation: FinancingBillingCreationType | UpfrontBillingCreationType,
): Promise<BillingType> => {
  const { data } = await platformAPI.post(`/billings`, billingCreation);

  return data;
};
